import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import Button from './components/Button'
import ButtonPrimary from './components/ButtonPrimary'
import { PersonCard, TraitCard } from './components/Cards'
import CookieBanner from './components/CookieBanner'
import {
  PersonCard as FigmaPersonCard,
  TraitCard as FigmaTraitCard,
} from './components/FigmaCards'
import { GameHero } from './components/GameHero'
import Header from './components/Header'
import { HeadingLarge } from './components/Heading'
import LanguageSwitcher from './components/LanguageSwitcher'
import NavigationText from './components/NavigationText'
import SwapButton from './components/SwapButton'
import {
  useCardAnimation,
  useCardPool,
  useCookieBanner,
  useGameState,
  useHubspotScript,
  useTracking,
} from './hooks'
import {
  CardContainer,
  Container,
  ControlsContainer,
  customHeadingMedium,
  customPrimaryButton,
  Footer,
  Half,
  Main,
  Root,
} from './style'

const App = ({ isFigma }) => {
  const { t } = useTranslation()
  const gameState = useGameState()
  const cardPool = useCardPool(gameState.locale)
  const cardAnimation = useCardAnimation(
    cardPool.person,
    cardPool.trait,
    gameState.animationDisabled,
  )
  const cookieBanner = useCookieBanner()
  const tracking = useTracking()
  useHubspotScript(isFigma)

  /**
   * Generate a new pair of cards (person and trait)
   */
  const generatePair = () => {
    regeneratePair('initial')
    gameState.setGameView(true)
  }

  /**
   * @param {*} actionType
   */
  const regeneratePair = (actionType) => {
    const { person, trait } = cardPool.dealNewPair()
    tracking.trackDealCards(actionType, person, trait)
  }

  /**
   * Toggle animation state
   */
  const toggleAnimation = () => {
    tracking.trackAnimationToggle(gameState.animationDisabled)
    gameState.setAnimationDisabled(!gameState.animationDisabled)
  }

  /**
   * @param {*} newLang
   */
  const switchLanguage = (newLang) => {
    gameState.changeLanguage(newLang)
    cardPool.resetPools(newLang)
  }

  return (
    <Root>
      <Suspense fallback="loading...">
        <Container $isFigma={isFigma}>
          {!isFigma && <Header />}
          <Main $isFigma={isFigma}>
            {gameState.gameView ? (
              <>
                {!isFigma && (
                  <HeadingLarge
                    as="h1"
                    $customStyle={customHeadingMedium}
                    data-testid="game-title"
                  >
                    {t('content.gameTitle')}
                  </HeadingLarge>
                )}
                <CardContainer>
                  <>
                    <Half $isFigma={isFigma}>
                      {cardAnimation.personTransitions((style, item) => {
                        const PersonCardRendered = isFigma
                          ? FigmaPersonCard
                          : PersonCard
                        return (
                          <PersonCardRendered
                            animationDisabled={gameState.animationDisabled}
                            person={item}
                            style={style}
                            onRefresh={cardPool.generateNewPerson}
                            onViewConsiderations={() => {}}
                            isFigma={isFigma}
                          />
                        )
                      })}
                    </Half>
                    <Half $isFigma={isFigma}>
                      {cardAnimation.traitTransitions((style, item) => {
                        const TraitCardRendered = isFigma
                          ? FigmaTraitCard
                          : TraitCard
                        return (
                          <TraitCardRendered
                            animationDisabled={gameState.animationDisabled}
                            trait={item}
                            style={style}
                            onRefresh={cardPool.generateNewTrait}
                            onViewConsiderations={() => {}}
                            isFigma={isFigma}
                          />
                        )
                      })}
                    </Half>
                  </>
                </CardContainer>
                {isFigma ? (
                  <ControlsContainer>
                    <SwapButton
                      onRefresh={cardPool.generateNewPerson}
                      dataTestId="swap-person-card-button"
                      text={t('content.swapCard')}
                    />
                    <ButtonPrimary
                      text={`Deal new pair`}
                      $customStyle={customPrimaryButton}
                      onClick={() => {
                        regeneratePair('new')
                      }}
                      data-testid="deal-new-pair-button"
                    />
                    <SwapButton
                      onRefresh={cardPool.generateNewTrait}
                      dataTestId="swap-trait-card-button"
                      text={t('content.swapCard')}
                    />
                  </ControlsContainer>
                ) : (
                  <ButtonPrimary
                    text={t('content.dealNewPair')}
                    $customStyle={customPrimaryButton}
                    onClick={() => {
                      regeneratePair('new')
                    }}
                    data-testid="deal-new-pair-button"
                  />
                )}
              </>
            ) : (
              <GameHero
                isFigma={isFigma}
                onGeneratePair={generatePair}
                animationDisabled={gameState.animationDisabled}
                onToggleAnimation={() =>
                  gameState.setAnimationDisabled(!gameState.animationDisabled)
                }
              />
            )}
          </Main>
          {!isFigma && (
            <Footer>
              <Button onClick={toggleAnimation} data-testid="animation-toggle">
                <NavigationText dataTestId="animation-toggle-text">
                  {gameState.animationDisabled
                    ? t('content.animationOff')
                    : t('content.animationOn')}
                </NavigationText>
              </Button>
              <div>
                <LanguageSwitcher onChange={switchLanguage} />
              </div>
            </Footer>
          )}
        </Container>
        {!isFigma && (
          <CookieBanner
            animationDisabled={gameState.animationDisabled}
            show={cookieBanner.showCookieBanner}
            onClose={cookieBanner.dismissCookieBanner}
          />
        )}
      </Suspense>
    </Root>
  )
}

export default App
